<template>
    <v-card elevation="0">
        <div v-if="fullCard" class="mb-5">
            <v-card-title class="text-h6 font-weight-light align-baseline">
                {{ $t('available_offers') }}
                <span class="ml-4 text-body-2 text-primary cursor-pointer" @click="expanded = !expanded">
                    {{ $t('filters') }}
                    <v-icon color="primary" size="small">
                        {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                </span>
            </v-card-title>
            <v-expand-transition v-model="expanded">
                <v-row v-if="expanded">
                    <v-col cols="12" md="4" class="mr-0 mr-md-3">
                        <sort-select v-model="sortKey" :items="sortItems" />
                    </v-col>
                    <v-col cols="12" md="3" class="mr-0 mr-md-3">
                        <group-offers-select
                            class="pt-3"
                            :items="['roomTypeName', 'mealTypeName', 'date', 'none']"
                            :product-store="productStore"
                        />
                    </v-col>
                    <v-col cols="12" md="3" class="pt-md-3">
                        <date-filter-search
                            v-model="dateFilter"
                            :dates-for-filter="datesForFilter"
                            @update:model-value="onDateFilterChange"
                        />
                    </v-col>
                </v-row>
            </v-expand-transition>
        </div>
        <v-expansion-panels v-if="groupOffersBy !== 'none'" v-model="panels" multiple variant="accordion">
            <v-expansion-panel v-for="(offers, groupIndex) in groupedOffers" :key="`group-${groupIndex}`">
                <v-expansion-panel-title
                    class="text-nobreak font-weight-bold"
                    :class="$breakpoint.smAndDown ? 'body-2' : 'subtitle-1'"
                    color="blue-grey-lighten-5"
                >
                    <div v-if="groupOffersBy === 'date'">
                        {{ $filters.dateFormat(offers[0][groupOffersBy]) }}
                    </div>
                    <div v-else>
                        {{ offers[0][groupOffersBy] }}
                    </div>
                    <div class="d-flex align-center justify-space-between font-weight-bold text-right pr-4 pl-2">
                        <v-chip
                            label
                            size="x-small"
                            :color="minPriceOffer(offers).available ? 'green-darken-1' : 'orange-lighten-3'"
                            class="mr-4 text-white"
                        >
                            {{ minPriceOffer(offers).available ? $t('available') : $t('on_request') }}
                        </v-chip>
                        <v-spacer />
                        <template v-if="minPriceOffer(offers).price && !hidePrice && !minPriceOffer.soldOut">
                            {{ $t('from') }} {{ $filters.price(minPriceOffer(offers).price) }}
                        </template>
                    </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                    <simple-tour-offer-card
                        v-for="(offer, offerIndex) in offers"
                        :key="offerIndex"
                        :offer="offer"
                        :full-card="fullCard"
                        :grouped-by="groupOffersBy"
                        :data-cy="`simple-tour-offer-card-${offerIndex}`"
                        @images="$emit('images', $event)"
                        @info="$emit('info', $event)"
                    >
                        <template v-slot:price="{offer}">
                            <slot name="price" :offer="offer" />
                        </template>
                    </simple-tour-offer-card>
                </v-expansion-panel-text>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-card>
</template>

<script lang="ts">
    import {Vue, Component, Emit, Prop, Watch} from 'vue-facing-decorator'
    import SortSelect from '@/components/search/SortSelect.vue'
    import GroupOffersSelect from '@/components/search/GroupOffersSelect.vue'
    import {minPriceOffer} from '~/utils/sort-helpers'
    import {simpleTourStore} from '~/composables/store-accessor'
    import SimpleTourOfferCard from '~/components/simpleTours/info/SimpleTourOfferCard.vue'
    import DateFilterSearch from '@/components/snippets/DateFilterSearch.vue'

    @Component({
        components: {
            DateFilterSearch,
            SimpleTourOfferCard,
            SortSelect,
            GroupOffersSelect,
        },
    })
    export class SimpleTourGroupedOffers extends Vue {
        @Prop({default: false, type: Boolean}) fullCard
        @Prop({default: false, type: Boolean}) hidePrice
        @Prop({default: () => []}) sortedFilteredOffers
        @Prop() datesForFilter

        dateFilter = null
        panels = [0]
        expanded = false

        @Watch('groupOffersBy')
        onGroupOffersByChange() {
            this.panels = [0]
        }

        minPriceOffer(offers) {
            return minPriceOffer({offers})
        }

        @Emit()
        setFilters(filter) {
            return filter
        }

        @Emit()
        onDateFilterChange(date) {
            return date
        }

        get searchResponse() {
            return this.productStore.searchResponse
        }

        get groupedOffers() {
            let offers = [...this.sortedFilteredOffers]

            return offers.reduce((groupedOffers, offer) => {
                let group = groupedOffers.find(group => group[0][this.groupOffersBy] === offer[this.groupOffersBy])
                if (!group) {
                    group = []
                    groupedOffers.push(group)
                }
                group.push(offer)
                return groupedOffers
            }, [])
        }

        get productStore() {
            return simpleTourStore
        }

        get groupOffersBy() {
            return this.productStore.groupOffersBy
        }

        get sortKey() {
            return this.productStore.sortFnName
        }

        set sortKey(sortKey) {
            this.productStore.SET_SORT(sortKey)
        }

        get sortItems() {
            return [
                {
                    text: this.$t('sort.price_asc'),
                    value: 'priceAsc',
                },
                {
                    text: this.$t('sort.price_desc'),
                    value: 'priceDesc',
                },
                {
                    text: this.$t('sort.date_asc'),
                    value: 'dateAsc',
                },
                {
                    text: this.$t('sort.name'),
                    value: 'name',
                },
            ]
        }
    }

    export default SimpleTourGroupedOffers
</script>

<style scoped>
    :deep(.v-expansion-panel-content__wrap) {
        padding-left: 0;
        padding-right: 0;
    }
</style>
